import { TabConfig } from '@/types/display';
import { Filter, LoginResult, Member } from '@vivport/backend/types';
import { useMemo } from 'react';
import { isHubAdmin } from '../utils/auth-utils';
import { useCheckAuth } from './useAuthQueries';
import { useTabFilters } from './useFilterQueries';

/**
 * Enhanced user information interface
 * Includes all computed properties for easy access
 */
export interface UserInfo extends LoginResult {
  selectedMember: Member;
  accountTabs: TabConfig[];
  defaultTab: TabConfig | null;
  tabFilters: Filter[];
  isHubAdmin: boolean;
  accessToken: string;
  refreshToken: string;
}

/**
 * useUser Hook
 * 
 * Enhanced version of useCheckAuth with computed tab/filter properties.
 * 
 * Returns comprehensive UserInfo or null if not authenticated.
 */
function filterToTabConfig(f: Filter): TabConfig {
  const value = f.shortName ?? '';
  const searchFilter = value ? `tab:${value} ` : '';
  const def = f.definition as { tabLabel?: string; tabLabelMobile?: string };
  return {
    id: f.id,
    value,
    isDefault: f.isDefaultTab ?? false,
    label: def?.tabLabel ?? f.name,
    labelMobile: def?.tabLabelMobile ?? f.name,
    searchFilter
  };
}

export const useUser = () => {
  const userQuery = useCheckAuth();
  const tabFiltersQuery = useTabFilters(userQuery.data?.selectedMember?.id, userQuery.data?.selectedMember?.hubId);

  const userInfo = useMemo((): UserInfo | null => {
    if (!userQuery.data) {
      return null;
    }

    const { selectedMember } = userQuery.data;

    const hubAdmin = isHubAdmin(selectedMember);

    // Derive accountTabs and defaultTab from tab-filters API only (no hardcoded fallback)
    let accountTabs: TabConfig[];
    let defaultTab: TabConfig | null;
    let tabFilters: Filter[] = [];

    if (tabFiltersQuery.data && tabFiltersQuery.data.length > 0) {
      tabFilters = tabFiltersQuery.data;
      accountTabs = tabFilters.map(filterToTabConfig);
      defaultTab = accountTabs.find(t => t.isDefault) ?? accountTabs[0] ?? null;
    } else {
      // Loading, error, or empty: no fallback - use empty tabs
      accountTabs = [];
      defaultTab = null;
    }

    return {
      ...userQuery.data,
      accountTabs,
      defaultTab,
      tabFilters,
      isHubAdmin: hubAdmin,
    };
  }, [userQuery.data, tabFiltersQuery.data, tabFiltersQuery.isLoading, tabFiltersQuery.isError]);

  return {
    ...userQuery,
    data: userInfo,
    isLoading: userQuery.isLoading || tabFiltersQuery.isLoading,
  };
};
